import React from 'react'
import {
  Bullet,
  Bullets,
  Info,
  Page,
  Screenshot,
  Section,
} from '../../components/DocsComponents'
import DocLink from '../../components/DocLink'

const HomeScreen = () => (
  <Page
    title="Home Screen"
    intro="Signing in to AmbuNet lands you on the Home Screen. It's built from
    cards that appear and disappear depending on your role and what's
    outstanding, so two users in the same organisation can see quite different
    home screens."
  >
    <Section title="What you'll see">
      <Screenshot url="getting-started/home-dashboard.png">
        The Home Screen with the To Do bar, Quick Links, Team Actions tiles,
        and the CQC readiness card.
      </Screenshot>
      <p>The Home Screen can show up to eight areas:</p>
      <Bullets>
        <Bullet>
          <strong>Finish setting up your organisation</strong> — a progress
          card shown to Site Administrators while the Getting Started checklist
          is incomplete.
        </Bullet>
        <Bullet>
          <strong>On Call Now</strong> — who's currently on an on-call rota,
          with a tap-to-call action.
        </Bullet>
        <Bullet>
          <strong>To Do</strong> — a bar of items assigned specifically to you.
        </Bullet>
        <Bullet>
          <strong>Active Shift</strong> — today's shift with clock in / clock
          out, when you have one.
        </Bullet>
        <Bullet>
          <strong>Active CAD Resource</strong> — shown when a dispatcher has
          crewed you on a Command &amp; Control resource in a live control
          room: your call sign, control room, crew, and current tasking, with
          a click-through to the crew view. It appears as soon as you're
          crewed, clocked in or not.
        </Bullet>
        <Bullet>
          <strong>Quick Links</strong> — one-click access to the most common
          actions.
        </Bullet>
        <Bullet>
          <strong>Team Actions</strong> — outstanding operational and clinical
          work for your teams, with live counts.
        </Bullet>
        <Bullet>
          <strong>CQC Readiness</strong> — readiness rings for Governance team
          members and Site Administrators, on plans that include the CQC
          Centre, with a click-through to the full CQC dashboard.
        </Bullet>
      </Bullets>
      <Info title="Choosing which bases you're looking at">
        The base location picker lives in the top bar (not on the Home Screen
        itself) and follows you around the whole app. Narrowing it changes
        what's shown in base-scoped areas such as On Call Now.
      </Info>
      <Info title="Getting around from here">
        The Home Screen is one of several ways in. For the ⌘K global search,
        the sidebar's badges and favourites, the Quick Add menu, and setting
        your own landing page, see{' '}
        <DocLink
          docsPage
          url="/getting-started/finding-your-way"
          text="Finding Your Way Around"
        />
        .
      </Info>
    </Section>

    <Section title="On Call Now">
      <p>
        If your organisation runs on-call rotas, anyone currently on call
        appears here with their name, base, rota, and shift end time — scoped
        to your selected base locations. Tapping an entry calls them directly:
        the base's shared standby line if one has been set (shown with a{' '}
        <strong>Site line</strong> chip), otherwise their personal mobile
        number.
      </p>
      <Info>
        This card only appears when someone is on call. Your organisation
        controls whether it's visible to everyone, restricted to the Manager
        and Supervisor teams, or hidden entirely — see{' '}
        <DocLink
          docsPage
          url="/employees-hr/rostering-payroll"
          text="Rostering, Availability, and Payroll"
        />{' '}
        for on-call rota planning.
      </Info>
    </Section>

    <Section title="To Do">
      <p>
        The <strong>To Do</strong> bar summarises items assigned specifically
        to you, each with a badge count and a click-through to the right page:
      </p>
      <Bullets>
        <Bullet>Announcements to acknowledge</Bullet>
        <Bullet>Drug bags checked out to you</Bullet>
        <Bullet>Tasks assigned to you, and your team's tasks</Bullet>
        <Bullet>Patient care records (ePCRs) still in draft</Bullet>
        <Bullet>Record audit results waiting for you to review</Bullet>
        <Bullet>Policies to acknowledge and SOPs to review</Bullet>
        <Bullet>Unread PGDs</Bullet>
        <Bullet>Unread Crew Vault items shared with you</Bullet>
        <Bullet>CQC gaps assigned to you</Bullet>
        <Bullet>Defects assigned to you</Bullet>
        <Bullet>Incomplete or expired mandatory training</Bullet>
        <Bullet>An appraisal waiting for you to complete</Bullet>
        <Bullet>An occupational health screening to complete</Bullet>
      </Bullets>
      <Info>
        The To Do bar is unique to your account and only appears when there's
        something outstanding.
      </Info>
    </Section>

    <Section title="Active Shift">
      <p>
        When you have a shift today, an Active Shift card shows its details —
        times, location, and vehicle where relevant — with a{' '}
        <strong>Clock In</strong> / <strong>Clock Out</strong> button. Clocking
        in opens shortly before the shift start time (15 minutes by default —
        your organisation can adjust the window), and clocking in late or
        finishing late prompts for a reason so managers have the context.
      </p>
      <Info title="PTS crews">
        If you're on a Patient Transport duty, you can't clock out while any of
        your journeys are still open — complete or hand over the remaining
        journeys first.
      </Info>
    </Section>

    <Section title="Quick Links">
      <p>Everyone sees these one-click actions:</p>
      <Bullets>
        <Bullet>
          <strong>Patient Care Record</strong> — create a new ePCR or continue
          a draft
        </Bullet>
        <Bullet>
          <strong>Meds Management</strong> — sign out, return, or check
          medicine bags
        </Bullet>
        <Bullet>
          <strong>Report a Defect</strong> — log equipment or vehicle faults
        </Bullet>
        <Bullet>
          <strong>Report an Incident</strong> — submit a clinical or
          operational incident
        </Bullet>
        <Bullet>
          <strong>Speak Up</strong> — raise a concern, anonymously by default
          (hidden if your organisation has disabled Speak Up)
        </Bullet>
        <Bullet>
          <strong>Start of Shift Check</strong> — complete your start-of-shift
          vehicle check
        </Bullet>
      </Bullets>
      <p>Some links depend on your role:</p>
      <Bullets>
        <Bullet>
          <strong>My PTS Duty</strong> — shown to any staff member on the
          Enterprise tier (crew work needs no special PTS permission)
        </Bullet>
        <Bullet>
          <strong>Make Ready</strong> — shown to the Fleet team
        </Bullet>
        <Bullet>
          <strong>Vehicle Clean</strong> — shown to employees (not
          subcontractors)
        </Bullet>
        <Bullet>
          <strong>Record Fuel</strong> — shown to employees (not
          subcontractors), for logging a vehicle refuel
        </Bullet>
      </Bullets>
      <p>
        Your organisation can add its own links via{' '}
        <DocLink
          text="Configuration > Quick Links"
          url="/site-configuration/quick-links"
        />
        .
      </p>
    </Section>

    <Section title="Team Actions">
      <p>
        Team Actions highlights outstanding operational and clinical work for
        your teams, with a live count on every tile and a click-through to deal
        with it. Depending on your teams, tiles can include:
      </p>
      <Bullets>
        <Bullet>Imaging Approvals awaiting a decision</Bullet>
        <Bullet>Safety Alerts awaiting acknowledgement</Bullet>
        <Bullet>New Incidents and H&amp;S Incidents to review</Bullet>
        <Bullet>New Safeguarding concerns from patient records</Bullet>
        <Bullet>New Patient Feedback</Bullet>
        <Bullet>New Speak Up concerns to review</Bullet>
        <Bullet>ePCRs Awaiting Audit</Bullet>
        <Bullet>New Defects, Services Overdue / Due Soon</Bullet>
        <Bullet>Deep Cleans Overdue</Bullet>
        <Bullet>
          Expired or Expiring Medicines, Open Drugs Bags, and Audit
          Discrepancies
        </Bullet>
        <Bullet>Make Ready items expired or expiring</Bullet>
        <Bullet>
          Shift Applications, Shift Swap Requests, and Uncovered Shifts
        </Bullet>
        <Bullet>Overtime Claims awaiting review</Bullet>
        <Bullet>Pending Mandatory Training and Appraisals to Review</Bullet>
        <Bullet>Pending employee Applications and Client Accounts</Bullet>
        <Bullet>Users Without Base Locations</Bullet>
      </Bullets>
      <Info>
        Which tiles appear depends on your assigned teams, your role, and your
        organisation's active modules — the section is hidden entirely when
        there's nothing outstanding for you.
      </Info>
    </Section>

    <Section title="Managers: the Global Overview">
      <p>
        Members of the Manager or Supervisor teams (and Site Administrators)
        also get a <strong>Global Overview</strong> entry in the sidebar — an
        organisation-wide dashboard of KPIs, charts, and items needing
        attention. See{' '}
        <DocLink
          docsPage
          url="/getting-started/global-overview"
          text="Global Overview"
        />
        .
      </p>
    </Section>
  </Page>
)

export default HomeScreen
